/**
 * 11 §7 animation layering — a pure clip/blend resolver. Lower body (locomotion)
 * and upper body (throw, shield, exhaust, idle arms) are independent layers;
 * DASHING and DEREZ override both. Every clip carries `t` (ms into the clip) and
 * a blend `weight`; `cur.weight + (prev?.weight ?? 0) === 1` at all times.
 * Nothing here reads a clock or the DOM: the caller supplies `nowMs` and `dtMs`
 * in the world render-time domain, so hitstop freezes the pose for free.
 */
import { MOVE_SPEED, SHIELD_PERFECT_TICKS, TICK_DT } from '../../../sim/constants.js'
import type { PlayerStateName } from '../../../sim/types.js'

export type LowerClip = 'idle' | 'run_fwd' | 'run_back' | 'strafe_l' | 'strafe_r'
export type UpperClip = 'idle_arms' | 'windup' | 'follow' | 'shield_raise' | 'shield_hold' | 'shield_lower' | 'exhaust'
export interface Blend<C> { clip: C; t: number; weight: number }
export interface ClipState {
  lower: { cur: Blend<LowerClip>; prev: Blend<LowerClip> | null; stridePhase: number }
  upper: { cur: Blend<UpperClip>; prev: Blend<UpperClip> | null }
  whole: 'none' | 'dash' | 'derez' | 'jump'
}
export interface ClipInput {
  state: PlayerStateName
  stateTicks: number
  /** |vel| / MOVE_SPEED. */
  speedFrac: number
  /** `gaitAngle(vel, aim)`: angle from velocity to aim (rad); 0 = running toward aim. */
  gaitRad: number
  alive: boolean
  shieldAge: number | null
  /** Render-time ms of the last `throw` event for this player (reconstructs a skipped windup), or null. */
  throwStartedMs: number | null
  nowMs: number
  /** S10 jump (11 §7): airborne flag, height (m) and landing-lock ticks left. */
  grounded: boolean
  y: number
  landTicks: number
}

export const BLEND_MS = 120
export const DASH_IN_MS = 30
export const DASH_OUT_MS = 60
/** Stride length in metres (matches AUDIO_STRIDE_M so footsteps and feet agree). */
export const STRIDE_M = 0.55
/** Strides per second at full MOVE_SPEED. */
export const MOVE_SPEED_PER_STRIDE = MOVE_SPEED / STRIDE_M
/** 11 §7 clip lengths (ms). */
export const WINDUP_MS = 50
export const FOLLOW_MS = 167
export const SHIELD_RAISE_MS = 34
export const SHIELD_LOWER_MS = 34
export const EXHAUST_MS = 1000
/** |vel| below this (m/s) is idle (11 §7). */
export const IDLE_SPEED = 0.3

const TICK_MS = TICK_DT * 1000

export function initialClips(nowMs: number, inp?: ClipInput): ClipState {
  void nowMs
  if (!inp) return { lower: { cur: { clip: 'idle', t: 0, weight: 1 }, prev: null, stridePhase: 0 }, upper: { cur: { clip: 'idle_arms', t: 0, weight: 1 }, prev: null }, whole: 'none' }
  // Settled on the input's clips (a player first seen mid-run does not blend up from idle); the
  // upper clip starts at the age the sim reports so a sampled state lands mid-clip, and the stride
  // phase is a deterministic function of render time so a frozen frame still shows a mid-stride.
  const upper = upperFor(inp)
  const state: ClipState = {
    lower: { cur: { clip: lowerFor(inp), t: 0, weight: 1 }, prev: null, stridePhase: wrap01((inp.nowMs / 1000) * inp.speedFrac * (MOVE_SPEED / STRIDE_M)) },
    upper: { cur: { clip: upper, t: upperSeedMs(upper, inp), weight: 1 }, prev: null },
    whole: wholeFor(inp),
  }
  return state
}

const wrap01 = (v: number): number => ((v % 1) + 1) % 1

/** Nearest 4-way locomotion clip; `strafe_l` = aim lies 90° counter-clockwise of travel (gaitRad ≈ +π/2). */
export function lowerFor(inp: ClipInput): LowerClip {
  if (inp.speedFrac * MOVE_SPEED < IDLE_SPEED) return 'idle'
  const g = Math.atan2(Math.sin(inp.gaitRad), Math.cos(inp.gaitRad)) // normalise to (−π, π]
  const a = Math.abs(g)
  if (a <= Math.PI / 4) return 'run_fwd'
  if (a >= (3 * Math.PI) / 4) return 'run_back'
  return g > 0 ? 'strafe_l' : 'strafe_r'
}

export function upperFor(inp: ClipInput): UpperClip {
  if (inp.throwStartedMs !== null) {
    const age = inp.nowMs - inp.throwStartedMs
    if (age >= 0 && age < WINDUP_MS) return 'windup'
    if (age >= WINDUP_MS && age < WINDUP_MS + FOLLOW_MS) return 'follow'
  }
  switch (inp.state) {
    case 'THROW_WINDUP': return 'windup'
    case 'THROW_RECOVERY': return 'follow'
    case 'SHIELD': return (inp.shieldAge ?? inp.stateTicks) <= 2 ? 'shield_raise' : 'shield_hold'
    case 'EXHAUST': return 'exhaust'
    default: return 'idle_arms'
  }
}

function wholeFor(inp: ClipInput): ClipState['whole'] {
  if (!inp.alive || inp.state === 'DEREZ') return 'derez'
  if (inp.state === 'DASHING') return 'dash'
  // S10: tuck while airborne, land crouch while the landing lock runs (the upper layer keeps playing over it).
  if (!inp.grounded || inp.landTicks > 0) return 'jump'
  return 'none'
}

/** Where a freshly entered upper clip starts, from what the sim already says about its age. */
function upperSeedMs(clip: UpperClip, inp: ClipInput): number {
  if (inp.throwStartedMs !== null && (clip === 'windup' || clip === 'follow')) {
    const age = inp.nowMs - inp.throwStartedMs
    return clip === 'windup' ? Math.max(0, age) : Math.max(0, age - WINDUP_MS)
  }
  const stMs = Math.max(0, inp.stateTicks) * TICK_MS
  switch (clip) {
    case 'windup': return inp.state === 'THROW_WINDUP' ? Math.min(WINDUP_MS, stMs) : 0
    case 'follow': return inp.state === 'THROW_RECOVERY' ? Math.min(FOLLOW_MS, stMs) : 0
    case 'shield_raise': return Math.min(SHIELD_RAISE_MS, (inp.shieldAge ?? inp.stateTicks) * TICK_MS)
    case 'shield_hold': return Math.max(0, ((inp.shieldAge ?? inp.stateTicks) - 2) * TICK_MS)
    case 'exhaust': return Math.min(EXHAUST_MS, stMs)
    default: return 0
  }
}

/** Switch `layer` to `next` with blend bookkeeping. `instant` drops the outgoing clip. */
function enter<C>(layer: { cur: Blend<C>; prev: Blend<C> | null }, next: C, t0: number, instant: boolean): void {
  if (instant) { layer.cur = { clip: next, t: t0, weight: 1 }; layer.prev = null; return }
  if (layer.prev && layer.prev.clip === next) {
    // Reversing mid-blend: swap so the weights stay continuous.
    const back = layer.prev
    layer.prev = layer.cur
    layer.cur = { clip: next, t: Math.max(t0, back.t), weight: back.weight }
    layer.cur.t = BLEND_MS * layer.cur.weight // re-derive the blend clock from the surviving weight
    return
  }
  layer.prev = { ...layer.cur }
  layer.cur = { clip: next, t: t0, weight: 0 }
}

/** Advance the blend clocks of one layer by dt and renormalise the weights (linear, BLEND_MS). */
function advance<C>(layer: { cur: Blend<C>; prev: Blend<C> | null }, dtMs: number, blendMs: number): void {
  layer.cur.t += dtMs
  if (layer.prev) {
    layer.prev.t += dtMs
    const w = Math.max(0, 1 - layer.cur.t / blendMs)
    if (w <= 0) { layer.prev = null; layer.cur.weight = 1 } else { layer.prev.weight = w; layer.cur.weight = 1 - w }
  } else layer.cur.weight = 1
}

export function resolveClips(prev: ClipState, inp: ClipInput, dtMs: number): ClipState {
  const dt = Math.max(0, dtMs)
  const whole = wholeFor(inp)
  // DEREZ: the pose is the last live frame (11 §7) — nothing advances.
  if (whole === 'derez') return { lower: { cur: { ...prev.lower.cur }, prev: prev.lower.prev ? { ...prev.lower.prev } : null, stridePhase: prev.lower.stridePhase }, upper: { cur: { ...prev.upper.cur }, prev: prev.upper.prev ? { ...prev.upper.prev } : null }, whole }
  const s: ClipState = {
    lower: { cur: { ...prev.lower.cur }, prev: prev.lower.prev ? { ...prev.lower.prev } : null, stridePhase: prev.lower.stridePhase },
    upper: { cur: { ...prev.upper.cur }, prev: prev.upper.prev ? { ...prev.upper.prev } : null },
    whole,
  }
  // Lower body: 4-way locomotion, stride phase by |vel| / STRIDE_M so feet do not slide.
  const lo = lowerFor(inp)
  if (lo !== s.lower.cur.clip) enter(s.lower, lo, 0, false)
  advance(s.lower, dt, BLEND_MS)
  s.lower.stridePhase = wrap01(s.lower.stridePhase + inp.speedFrac * (MOVE_SPEED / STRIDE_M) * (dt / 1000))
  // Upper body.
  let up = upperFor(inp)
  const pc = s.upper.cur
  if (up === 'idle_arms') {
    if (pc.clip === 'shield_raise' || pc.clip === 'shield_hold') up = 'shield_lower'
    else if (pc.clip === 'shield_lower' && pc.t + dt < SHIELD_LOWER_MS) up = 'shield_lower'
  }
  if (up !== pc.clip) enter(s.upper, up, Math.max(0, upperSeedMs(up, inp) - dt), up === 'windup') // seed − dt: advance() adds this frame back
  advance(s.upper, dt, BLEND_MS)
  return s
}

/** 11 §5 perfect window length in ms (exported for pose code that brightens the raise). */
export const SHIELD_PERFECT_MS = SHIELD_PERFECT_TICKS * TICK_MS
